import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, photoSrc, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import DepositModal from "../components/DepositModal";
import GiftPicker from "../components/GiftPicker";
import { ArrowLeft, Send, Loader2, MoreVertical, UserX, Ban, Flag, Check, CheckCheck, Sparkles, Gift } from "lucide-react";

const ICEBREAKERS = ["Hey! 👋", "What's your favourite spot in Gulbarga?", "Chai or coffee? ☕"];
const REPORT_REASONS = ["Fake profile", "Inappropriate messages", "Harassment", "Spam or scam", "Other"];

export default function ChatRoom() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscribe, send: wsSend, isOnline } = useSocket();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [otherLastRead, setOtherLastRead] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [access, setAccess] = useState(null);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showGifts, setShowGifts] = useState(false);
  const [sendingGift, setSendingGift] = useState(false);
  const [depositNotice, setDepositNotice] = useState(null);
  const endRef = useRef(null);
  const typingTimer = useRef(null);
  const lastTypingSent = useRef(0);

  const markRead = useCallback(() => {
    api.post(`/matches/${matchId}/read`).catch(() => {});
  }, [matchId]);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/matches");
      const m = data.matches.find((x) => x.match_id === matchId);
      if (m) setOther(m.user);
      else navigate("/matches", { replace: true });
    } catch {}
  }, [matchId, navigate]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}/messages`);
      setMessages(data.messages);
      if (data.other_last_read) setOtherLastRead(data.other_last_read);
      markRead();
    } catch {} finally { setLoading(false); }
  }, [matchId, markRead]);

  const loadAccess = useCallback(async () => {
    try { const { data } = await api.get(`/matches/${matchId}/message-access`); setAccess(data); }
    catch {}
  }, [matchId]);

  useEffect(() => {
    loadMeta();
    loadMessages();
    loadAccess();
    const poll = setInterval(loadMessages, 30000);

    const unsub = subscribe((data) => {
      if (data.match_id !== matchId) return;
      if (data.type === "message") {
        setMessages((prev) => (prev.some((m) => m.message_id === data.message.message_id) ? prev : [...prev, data.message]));
        if (data.message.sender_id !== user.user_id) markRead();
      } else if (data.type === "typing") {
        setTyping(true);
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setTyping(false), 2500);
      } else if (data.type === "read") {
        if (data.user_id !== user.user_id) setOtherLastRead(data.last_read);
      } else if (data.type === "unmatched") {
        toast("This conversation has ended");
        navigate("/matches", { replace: true });
      }
    });

    return () => { clearInterval(poll); clearTimeout(typingTimer.current); unsub(); };
  }, [loadMeta, loadMessages, matchId, user.user_id, markRead, navigate, subscribe]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const sendTyping = () => {
    const now = Date.now();
    if (now - lastTypingSent.current < 1500) return;
    lastTypingSent.current = now;
    wsSend({ type: "typing", match_id: matchId });
  };

  const online = other ? isOnline(other.user_id) : false;
  const lastMsg = messages[messages.length - 1];
  const showReceipt = lastMsg && lastMsg.sender_id === user.user_id && !String(lastMsg.message_id).startsWith("tmp_");
  const lastSeen = showReceipt && otherLastRead && lastMsg.created_at <= otherLastRead;

  const send = async (value) => {
    const body = (value ?? text).trim();
    if (!body) return;
    // Paywall: real-user first message needs ₹100 balance.
    if (access && access.requires_payment && !access.unlocked && access.deposit_balance < access.price) {
      setShowDeposit(true);
      return;
    }
    setText("");
    setSuggestions([]);
    const optimistic = { message_id: `tmp_${Date.now()}`, sender_id: user.user_id, text: body, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const { data } = await api.post(`/matches/${matchId}/messages`, { text: body });
      if (data.charged) { toast.success(`₹${access?.price || 20} message credit used`); }
      loadAccess();
      loadMessages();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
      if (e.response?.status === 402) { setText(body); setShowDeposit(true); }
      else toast.error("Could not send message");
    }
  };

  const sendGift = async (gift) => {
    if (sendingGift) return;
    setSendingGift(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/gift`, { gift_id: gift.id });
      toast.success(`${gift.emoji} ${gift.label} sent · ₹${gift.price}`);
      setShowGifts(false);
      setMessages((prev) => (prev.some((m) => m.message_id === data.message.message_id) ? prev : [...prev, data.message]));
      loadAccess();
    } catch (e) {
      if (e.response?.status === 402) {
        setShowGifts(false);
        setDepositNotice(`Deposit ₹${gift.price} to send ${gift.emoji} ${gift.label} to ${other?.name || ""}.`);
        setShowDeposit(true);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send gift");
      }
    } finally {
      setSendingGift(false);
    }
  };

  const getSuggestions = async () => {
    setSuggestions([]);
    setSuggesting(true);
    try {
      const { data } = await api.post("/ai/chat-suggestions", { match_id: matchId });
      setSuggestions(data.suggestions || []);
    } catch { toast.error("AI couldn't suggest replies right now"); }
    finally { setSuggesting(false); }
  };

  const doUnmatch = async () => {
    try { await api.post(`/unmatch/${matchId}`); toast("Unmatched"); navigate("/matches", { replace: true }); }
    catch { toast.error("Could not unmatch"); }
  };

  const doBlock = async () => {
    try { await api.post(`/block/${other.user_id}`); toast("User blocked"); navigate("/matches", { replace: true }); }
    catch { toast.error("Could not block"); }
  };

  const doReport = async (reason) => {
    try {
      await api.post("/report", { target_user_id: other.user_id, reason, block: true });
      toast.success("Report submitted. User blocked.");
      navigate("/matches", { replace: true });
    } catch { toast.error("Could not submit report"); }
  };

  const avatar = other?.photos?.find((p) => p.is_primary)?.url || other?.photos?.[0]?.url;

  return (
    <>
      <header className="h-16 px-3 bg-[#0D0C12]/90 backdrop-blur-xl border-b border-white/5 flex items-center gap-3 z-30 shrink-0">
        <button data-testid="chat-back-button" onClick={() => navigate("/matches")} className="w-9 h-9 grid place-items-center text-slate-300 rounded-full active:bg-white/10">
          <ArrowLeft className="w-5 h-5" />
        </button>
        {other && (
          <>
            <div className="relative shrink-0">
              <img src={photoSrc(avatar)} alt="" className="w-10 h-10 rounded-full object-cover" />
              {online && <span data-testid="chat-online-dot" className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0D0C12]" />}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-white leading-tight truncate">{other.name}</p>
              <p className={`text-xs ${typing || online ? "text-emerald-400" : "text-slate-500"}`}>
                {typing ? "typing…" : online ? "Online now" : "Offline"}
              </p>
            </div>
          </>
        )}
        <div className="ml-auto relative">
          <button data-testid="chat-menu-button" onClick={() => setMenuOpen((o) => !o)}
            className="w-9 h-9 grid place-items-center text-slate-300 rounded-full active:bg-white/10">
            <MoreVertical className="w-5 h-5" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-11 z-50 w-44 rounded-2xl bg-[#1D1829] border border-white/10 shadow-2xl p-1.5">
                {access?.is_bot && (
                  <div data-testid="ai-disclosure-info" className="flex items-center gap-2 px-3 py-2 mb-1 rounded-xl bg-white/[0.04]">
                    <Sparkles className="w-3.5 h-3.5 text-fuchsia-300 shrink-0" />
                    <span className="text-[11px] text-slate-400 leading-tight">AI-assisted chat</span>
                  </div>
                )}
                <MenuItem testid="chat-unmatch-button" icon={UserX} label="Unmatch" onClick={() => { setMenuOpen(false); doUnmatch(); }} />
                <MenuItem testid="chat-block-button" icon={Ban} label="Block" danger onClick={() => { setMenuOpen(false); doBlock(); }} />
                <MenuItem testid="chat-report-button" icon={Flag} label="Report" danger onClick={() => { setMenuOpen(false); setReportOpen(true); }} />
              </div>
            </>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 flex flex-col gap-2.5" data-testid="chat-messages">
        {loading ? (
          <div className="flex-1 grid place-items-center"><Loader2 className="w-6 h-6 text-rose-500 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 px-6">
            <p className="text-slate-400 text-sm">You matched! Break the ice 👇</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {ICEBREAKERS.map((i) => (
                <button key={i} onClick={() => send(i)} className="px-3.5 py-2 rounded-full bg-white/5 border border-rose-400/30 text-rose-200 text-sm active:scale-95 transition-transform">{i}</button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user.user_id;
            if (m.kind === "gift") {
              return (
                <div key={m.message_id} data-testid="gift-message"
                  className={`max-w-[75%] px-4 py-3 rounded-2xl ${mine ? "self-end bg-gradient-to-br from-rose-500/30 to-fuchsia-500/20 border border-rose-400/40 rounded-br-md" : "self-start bg-[#241b30] border border-fuchsia-400/30 rounded-bl-md"}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl leading-none">{m.gift_emoji}</span>
                    <div>
                      <p className="text-sm font-bold text-white">{mine ? "You sent" : "Sent you"} a {m.gift_label}</p>
                      <p className="text-[11px] text-rose-200/90">₹{m.gift_price} gift</p>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={m.message_id} className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-snug ${mine ? "self-end bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-br-md" : "self-start bg-[#1E1A2B] text-slate-100 rounded-bl-md"}`}>
                {m.text}
              </div>
            );
          })
        )}
        {showReceipt && (
          <div data-testid="read-receipt" className="self-end flex items-center gap-1 text-[11px] text-slate-500 pr-1 -mt-1">
            {lastSeen ? (<><CheckCheck className="w-3.5 h-3.5 text-rose-400" />Seen</>) : (<><Check className="w-3.5 h-3.5" />Delivered</>)}
          </div>
        )}
        {typing && !loading && (
          <div data-testid="typing-indicator" className="self-start bg-[#1E1A2B] rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1">
            {[0, 1, 2].map((i) => (
              <span key={i} className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {suggestions.length > 0 && (
        <div data-testid="ai-suggestions" className="px-3 pt-2 flex flex-wrap gap-2 bg-[#13101C]/95 backdrop-blur-xl border-t border-white/10 shrink-0">
          {suggestions.map((s, i) => (
            <button key={i} data-testid={`ai-suggestion-${i}`} onClick={() => send(s)}
              className="px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-400/30 text-rose-100 text-xs active:scale-95 transition-transform text-left">
              {s}
            </button>
          ))}
        </div>
      )}
      {access && access.requires_payment && !access.unlocked && (
        <div data-testid="chat-paywall-banner" className={`px-4 py-2.5 shrink-0 border-t border-amber-400/20 ${access.deposit_balance < access.price ? "bg-rose-500/10" : "bg-amber-500/10"}`}>
          {access.deposit_balance < access.price ? (
            <div className="flex items-center gap-3">
              <p className="text-xs text-rose-200 font-medium flex-1">Deposit ₹{access.price} to unlock messaging {other?.name}.</p>
              <button data-testid="chat-paywall-deposit" onClick={() => setShowDeposit(true)}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-bold active:scale-95 transition-transform">Deposit ₹{access.price}</button>
            </div>
          ) : (
            <p className="text-[11px] text-amber-200/90 text-center">Your first message uses ₹{access.price} of your balance.</p>
          )}
        </div>
      )}
      <div className={`px-3 py-3 bg-[#13101C]/95 backdrop-blur-xl flex items-center gap-2 shrink-0 ${suggestions.length || (access && access.requires_payment && !access.unlocked) ? "" : "border-t border-white/10"}`}>
        <button data-testid="ai-suggest-button" onClick={getSuggestions} disabled={suggesting}
          className="w-11 h-11 rounded-full bg-white/5 border border-rose-400/30 grid place-items-center text-rose-300 active:scale-90 transition-transform shrink-0 disabled:opacity-60">
          {suggesting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
        </button>
        <button data-testid="chat-gift-button" onClick={() => setShowGifts(true)}
          className="w-11 h-11 rounded-full bg-white/5 border border-fuchsia-400/30 grid place-items-center text-fuchsia-300 active:scale-90 transition-transform shrink-0">
          <Gift className="w-5 h-5" />
        </button>
        <input
          data-testid="chat-message-input"
          value={text}
          onChange={(e) => { setText(e.target.value); sendTyping(); }}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message…"
          className="flex-1 h-11 px-4 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-rose-500/60"
        />
        <button data-testid="chat-send-button" onClick={() => send()}
          className="w-11 h-11 rounded-full bg-gradient-to-r from-rose-500 to-rose-600 grid place-items-center text-white active:scale-90 transition-transform shrink-0">
          <Send className="w-5 h-5" />
        </button>
      </div>

      {reportOpen && (
        <div className="absolute inset-0 z-50 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setReportOpen(false)} />
          <div data-testid="report-sheet" className="relative bg-[#15121E] border-t border-white/10 rounded-t-3xl p-6 pb-8 animate-float-up">
            <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Report {other?.name}</h3>
            <p className="text-sm text-slate-400 mb-4">They'll be blocked and won't see you again.</p>
            <div className="flex flex-col gap-2">
              {REPORT_REASONS.map((r) => (
                <button key={r} data-testid={`report-reason-${r.replace(/\s+/g, "-")}`} onClick={() => doReport(r)}
                  className="w-full text-left px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-slate-200 text-sm active:bg-white/10">
                  {r}
                </button>
              ))}
            </div>
            <button onClick={() => setReportOpen(false)} className="w-full mt-4 py-3 rounded-xl text-slate-400 text-sm font-semibold">Cancel</button>
          </div>
        </div>
      )}
      {showGifts && (
        <GiftPicker recipientName={other?.name} sending={sendingGift}
          onClose={() => setShowGifts(false)} onSend={sendGift} />
      )}
      {showDeposit && (
        <DepositModal notice={depositNotice || `Deposit ₹${access?.price || 20} to unlock messaging ${other?.name || ""}.`}
          onClose={() => { setShowDeposit(false); setDepositNotice(null); }}
          onSuccess={() => { setShowDeposit(false); setDepositNotice(null); loadAccess(); }} />
      )}
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium active:bg-white/5 ${danger ? "text-rose-400" : "text-slate-200"}`}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );
}
